import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Service, ServiceType } from '../../types/service';
import { ServiceFormData, createService, updateService } from '../../services/serviceService';
import Modal from '../common/Modal'; // سنفترض وجود مكون Modal عام

interface ServiceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null; // null يعني إنشاء جديد، وإلا فهو تعديل
}

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({ isOpen, onClose, service }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ServiceFormData>();

  // تعبئة النموذج بالبيانات عند فتح وضع التعديل
  useEffect(() => {
    if (service) {
      reset(service);
    } else {
      reset({ name: '', description: '', fee: 0, type: ServiceType.SUBSCRIBER });
    }
  }, [service, reset]);

  const mutation = useMutation({
    mutationFn: (data: ServiceFormData) => {
      return service ? updateService(service.id, data) : createService(data);
    },
    onSuccess: () => {
      toast.success(service ? 'تم تعديل الخدمة بنجاح!' : 'تم إنشاء الخدمة بنجاح!');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'فشلت العملية.');
    },
  });

  const onSubmit: SubmitHandler<ServiceFormData> = (data) => {
    // تحويل الرسوم إلى رقم
    const processedData = { ...data, fee: Number(data.fee) };
    mutation.mutate(processedData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={service ? 'تعديل خدمة' : 'إضافة خدمة جديدة'}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-field">
          <label>اسم الخدمة</label>
          <input {...register('name', { required: 'الاسم مطلوب' })} />
          {errors.name && <p className="error">{errors.name.message}</p>}
        </div>
        <div className="form-field">
          <label>الوصف</label>
          <textarea {...register('description')} />
        </div>
        <div className="form-field">
          <label>الرسوم</label>
          <input type="number" step="0.01" {...register('fee', { required: 'الرسوم مطلوبة', valueAsNumber: true })} />
          {errors.fee && <p className="error">{errors.fee.message}</p>}
        </div>
        <div className="form-field">
          <label>موجهة لـ</label>
          <select {...register('type', { required: 'هذا الحقل مطلوب' })}>
            <option value={ServiceType.SUBSCRIBER}>مشترك جديد</option>
            <option value={ServiceType.TRAINEE}>متدرب</option>
            <option value={ServiceType.LAWYER}>محامي</option>
          </select>
          {errors.type && <p className="error">{errors.type.message}</p>}
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose} className="button secondary">إلغاء</button>
          <button type="submit" disabled={mutation.isPending} className="button primary">
            {mutation.isPending ? 'جاري الحفظ...' : 'حفظ'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ServiceFormModal;
